import type {DragEndEvent} from '@dnd-kit/core'
import {designerReducer} from '@/components/form-layout/designer-reducer'
import {resolveFieldLabel, type DesignerAction, type LayoutField, type LayoutSchema, type Palette} from '@/types/form-layout'

export interface HandleLayoutDragEndArgs {
    event: DragEndEvent
    draft: LayoutSchema
    palette: Palette | null
    baseline: LayoutSchema
    dispatch: (action: DesignerAction) => void
    onReorder: (req: {nextSchema: LayoutSchema; baseline: LayoutSchema}) => void
    onActiveFieldClear: () => void
    onDuplicateField: (label: string) => void
}

/**
 * Pure DnD drag-end resolver for the form-layout designer.
 *
 * WHY: extracting the drag dispatch logic from the page lets us cover the
 * branch matrix (palette-drop / palette-drop-without-target / duplicate /
 * field-card same-section reorder / field-card cross-section move /
 * field-card same-index no-op / section reorder / section same-index no-op /
 * unknown drop / no-over) without firing pointer events through dnd-kit.
 */
export function handleLayoutDragEnd(args: HandleLayoutDragEndArgs): void {
    const {event, draft, palette, baseline, dispatch, onReorder, onActiveFieldClear, onDuplicateField} = args

    onActiveFieldClear()

    const {active, over} = event
    if (!over) return

    const activeData = active.data.current
    const overData = over.data.current

    if (activeData?.type === 'palette-item') {
        const field = activeData.field as LayoutField
        let targetSectionKey: string | null = null

        if (overData?.type === 'section-drop') {
            targetSectionKey = overData.sectionKey as string
        } else if (overData?.type === 'field-card') {
            targetSectionKey = overData.sectionKey as string
        }

        if (!targetSectionKey) {
            dispatch({type: 'ADD_SECTION'})
            queueMicrotask(() => {
                const lastSection = draft.sections[draft.sections.length - 1]
                if (lastSection) {
                    dispatch({type: 'ADD_FIELD', sectionKey: lastSection.key, field})
                }
            })
            return
        }

        const alreadyIn = draft.sections.some((s) =>
            s.fields.some((f) => {
                if (field.source === 'entity' && f.source === 'entity') return f.fieldKey === field.fieldKey
                if (field.source === 'attribute' && f.source === 'attribute') return f.definitionId === field.definitionId
                if (field.source === 'relationship' && f.source === 'relationship') return f.relationshipType === field.relationshipType
                return false
            }),
        )

        if (alreadyIn) {
            onDuplicateField(resolveFieldLabel(field, palette))
            return
        }

        dispatch({type: 'ADD_FIELD', sectionKey: targetSectionKey, field})
        return
    }

    if (activeData?.type === 'field-card' && overData?.type === 'field-card') {
        const fromSection = activeData.sectionKey as string
        const toSection = overData.sectionKey as string
        const fromIndex = activeData.fieldIndex as number
        const toIndex = overData.fieldIndex as number

        let nextSchema: LayoutSchema = draft

        if (fromSection === toSection) {
            if (fromIndex !== toIndex) {
                nextSchema = designerReducer(draft, {
                    type: 'REORDER_FIELDS',
                    sectionKey: fromSection,
                    fromIndex,
                    toIndex,
                })
            } else {
                return
            }
        } else {
            nextSchema = designerReducer(draft, {
                type: 'MOVE_FIELD',
                fromSectionKey: fromSection,
                toSectionKey: toSection,
                fieldIndex: fromIndex,
                toIndex,
            })
        }
        onReorder({nextSchema, baseline})
        return
    }

    if (activeData?.type === 'section' && overData?.type === 'section') {
        const fromKey = activeData.sectionKey as string
        const toKey = overData.sectionKey as string
        const fromIndex = draft.sections.findIndex((s) => s.key === fromKey)
        const toIndex = draft.sections.findIndex((s) => s.key === toKey)
        if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
            const nextSchema = designerReducer(draft, {type: 'REORDER_SECTIONS', fromIndex, toIndex})
            onReorder({nextSchema, baseline})
        }
    }
}
